import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

function Home() {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section className="min-h-screen pt-24 md:pt-28 pb-12 px-4 sm:px-6 lg:px-8">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto"
      >
        {/* Hero Section */}
        <motion.div variants={itemVariants} className="text-center mb-12 md:mb-16">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-6"
          >
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            Green Maker Lab
          </motion.div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6 leading-tight text-balance">
            {t('home.title')}
          </h1>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed text-pretty">
            {t('home.description')}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
        >
          {/* Card Principal - TPQVS */}
          <motion.div
            variants={itemVariants}
            className="md:col-span-2 lg:col-span-2 glass-card-hover p-6 md:p-8"
          >
            <div className="flex flex-col lg:flex-row gap-6 items-center">
              <div className="flex-1 space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-lg">
                  <span className="w-1.5 h-1.5 bg-primary rounded-full" />
                  <span className="text-xs font-medium text-primary uppercase tracking-wider">
                    {t('ui.tableHighlight')}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-text-primary">
                  {t('TpqvsLibras.title')}
                </h2>
                <p className="text-text-secondary leading-relaxed ">
                  {t('TpqvsLibras.description')}
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/TPQVS" className="btn-primary">
                    {t('ui.exploreTable')}
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <a
                    href="https://doi.org/10.20395/revesp.2025.63.1.136-150"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {t('TpqvsLibras.articleInLibres')}
                  </a>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="relative w-full lg:w-auto flex-shrink-0 cursor-pointer group"
                onClick={() => setIsModalOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setIsModalOpen(true)}
                aria-label={t('TpqvsLibras.viewFullSize')}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl blur-xl" />
                <img
                  src="/img/Tpqvs_Libras.png"
                  alt="TPQVS Libras"
                  className="relative w-full lg:w-80 h-auto rounded-2xl border border-glass-border shadow-card"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white/90 dark:bg-slate-900/90 rounded-lg text-gray-900 dark:text-white text-sm font-medium shadow-lg backdrop-blur-sm">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                    {t('TpqvsLibras.viewFullSize')}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Card Equipe */}
          <motion.div variants={itemVariants}>
            <Link to="/team" className="block h-full">
              <div className="glass-card-hover p-6 h-full flex flex-col justify-between group">
                <div>
                  <div className="w-12 h-12 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-text-primary mb-2">{t('header.team')}</h3>
                  <p className="text-text-secondary text-sm">
                    {t('home.knowUs')}
                  </p>
                </div>
                <div className="flex items-center text-primary text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                  {t('ui.viewTeam')}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Card Publicacoes */}
          <motion.div variants={itemVariants}>
            <Link to="/publications" className="block h-full">
              <div className="glass-card-hover p-6 h-full flex flex-col justify-between group">
                <div>
                  <div className="w-12 h-12 bg-accent/10 border border-accent/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                    <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-text-primary mb-2">{t('header.publications')}</h3>
                  <p className="text-text-secondary text-sm">
                    {t('ui.publicationsDescription')}
                  </p>
                </div>
                <div className="flex items-center text-accent text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                  {t('ui.viewPublications')}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Card Sobre */}
          <motion.div variants={itemVariants}>
            <Link to="/about-us" className="block h-full">
              <div className="glass-card-hover p-6 h-full flex flex-col justify-between group">
                <div>
                  <div className="w-12 h-12 bg-accent-pink/10 border border-accent-pink/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent-pink/20 transition-colors">
                    <svg className="w-6 h-6 text-accent-pink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-text-primary mb-2">{t('header.aboutUs')}</h3>
                  <p className="text-text-secondary text-sm">
                    {t('ui.aboutDescription')}
                  </p>
                </div>
                <div className="flex items-center text-accent-pink text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                  {t('ui.learnMore')}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Modal Full-Screen para TPQVS em Libras */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4"
            onClick={() => setIsModalOpen(false)}
          >
            {/* Backdrop com blur */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
            
            {/* Container do Modal - Maior */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-[98vw] md:w-[95vw] lg:w-[90vw] xl:w-[85vw] max-w-[1400px] max-h-[95vh] md:max-h-[92vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Botao de Fechar - Fixo no topo */}
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-10 md:-top-12 right-0 flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors z-10"
                aria-label={t('TpqvsLibras.closeModal')}
              >
                <span className="text-sm font-medium">{t('TpqvsLibras.closeModal')}</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Container da Imagem - Maior e com scroll */}
              <div className="bg-white rounded-xl md:rounded-2xl p-2 md:p-4 shadow-2xl overflow-auto max-h-[90vh] md:max-h-[88vh]">
                <img
                  src="/img/Tpqvs_Libras.png"
                  alt="TPQVS em Libras - Tamanho Completo"
                  className="w-full h-auto min-w-[600px] md:min-w-[800px] lg:min-w-0"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default Home;
